import { meta } from "./_meta";
import styles from './_hcp-dosing.module.scss';
import HcpDosingVariesByAge from "./varies-by-age";
import HcpLongButton from "@/components-hcp/long-button";
import HcpPageFootnote from "@/components-hcp/page-footnote";
import { useMemo } from "react";
import HcpDosingPharmacokinetic from "./pharmacokinetic";
import HcpDosingAdministration from "./administration";
import HcpDosingDowntitration from "./downtitration";
import HcpDosingGcCalculator from "@/components-hcp/gc-calculator";
import HcpGcConversionCalculator from "@/components-hcp/gc-conversion-calculator";
import HcpBodySurfaceAreaCalculator from "@/components-hcp/body-surface-area-calculator";
import HcpDosingAccessSimplified from "./access-simplified";
import HcpHero from "@/components-hcp/hero";
import HcpPageReferences from "@/components-hcp/page-references";
import Button from '@/components/button';
import CustomImage from '@/components/image';

// Bypass the browser's native hash-scroll (which ignores any offset) and
// instead scroll programmatically so the 32 px gap is applied immediately
// in one step, avoiding the two-animation race with scroll-behavior: smooth.
const ANCHOR_SCROLL_OFFSET = 32;

function scrollToAnchor(e, id) {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - ANCHOR_SCROLL_OFFSET;
  window.scrollTo({ top: y, behavior: 'smooth' });
}

export default function HcpDosing() {
  const references = useMemo(() => [
    <>Crenessity. Package insert. Neurocrine Biosciences, Inc.</>,
    <>Auchus RJ, Hamidi O, Pivonello R, et al. Phase 3 trial of crinecerfont in adult congenital adrenal hyperplasia. <em>N Engl J Med</em>. 2024;391(6):504-514. doi:10.1056/NEJMoa2404656</>,
    <>Auchus RJ, Sarafoglou K, Fechner PY, et al. Crinecerfont lowers elevated hormone markers in adults with 21-hydroxylase deficiency congenital adrenal hyperplasia. <em>J Clin Endocrinol Metab</em>. 2022;107(3):801-812. doi:10.1210/clinem/dgab749</>,
    <>Hamidi O, Auchus R, Correa R, et al. Glucocorticoid reduction after starting crinecerfont in adult patients with classic CAH: practical perspectives. <em>J Clin Endocrinol Metab</em>. Epub ahead of print April 1, 2026. Accessed May 20, 2026. doi:10.1210/clinem/dgag147</>,
    <>Data on file. Neurocrine Biosciences, Inc.</>,
    <>Beuschlein F, Else T, Bancos I, et al. European Society of Endocrinology and Endocrine Society Joint Clinical Guideline: Diagnosis and therapy of glucocorticoid-induced adrenal insufficiency. <em>Clin Endocrinol Metab</em>. 2024;109(7):1657-1683. doi:10.1210/clinem/dgae250</>,
    <>Lang K, Quinkler M, Kienitz T. Mineralocorticoid replacement therapy in salt-wasting congenital adrenal hyperplasia. <em>Clin Endocrinol (Oxf)</em>. 2024;101(4):346-358. doi:10.1111/cen.14959</>,
    <>Bancos I, Kim H, Cheng HK, et al. Glucocorticoid therapy in classic congenital adrenal hyperplasia: traditional and new treatment paradigms. <em>Expert Rev Endocrinol Metab</em>. 2025;20(1):33-49. doi:10.1080/17446651.2025.2450423</>,
    <>Speiser PW, Arlt W, Auchus RJ, et al. Congenital adrenal hyperplasia due to steroid 21-hydroxylase deficiency: an Endocrine Society clinical practice guideline. <em>J Clin Endocrinol Metab</em>. 2018;103(11):4043-4088. doi:10.1210/jc.2018-01865</>,
    <>Vaid S, Merke DP, Salam M, et al. Crinecerfont improves reproductive hormones in classic congenital adrenal hyperplasia: 1-year results from the CAHtalyst<sup>&reg;</sup> Adult Study. Poster presented at: American Association of Clinical Endocrinology Annual Meeting; May 15-17, 2025; Orlando, FL.</>,
    <>Sarafoglou K, Kim MS, Lodish M, et al. Phase 3 trial of crinecerfont in pediatric congenital adrenal hyperplasia. <em>N Engl J Med</em>. 2024;391(6):493-503. doi:10.1056/NEJMoa2404655</>,
    <>Jeha GS, Newfield RS, Geffner ME, et al. Crinecerfont maintains reductions in serum androstenedione levels and glucocorticoid doses in children and adolescents with classic congenital adrenal hyperplasia: 1-year results from the CAHtalyst<sup>&reg;</sup> Pediatric Study. Presented at: Pediatric Endocrine Society Annual Meeting (PES 2025); April 30-May 3, 2025; San Francisco, CA.</>,
    <>Sarafoglou K, Bouvattier C, Capalbo D, et al. Long-term crinecerfont treatment enables sustained decrease in glucocorticoid doses and improves clinical outcomes with classic congenital adrenal hyperplasia: 2 year results from CAHtalyst<sup>&reg;</sup> pediatric. Presented at: The Endocrine Society Annual Meeting. June 13-16, 2026; Chicago, IL.</>,
    <>Bancos I, Hamidi O, Kiefer FW, et al. Adults with classic congenital adrenal hyperplasia taking crinecerfont demonstrated sustained decreased in glucocorticoid doses: 2-year results from the CAHtalyst<sup>&reg;</sup> adult study. Presented at: The Endocrine Society Annual Meeting. June 13-16, 2026; Chicago, IL.</>,
    <><em>Adrenal Insufficiency: Identification and Management</em>. London, UK: National Institute for Health and Care Excellence (NICE); August 28, 2024.</>,
    <>Hospital for Sick Children. Stress dosing or illness dosing for adrenal insufficiency. SickKids. Accessed July 21, 2025. https://www.sickkids.ca/siteassets/care--services/clinical-departments/endocrinology/stress-dosing-illness-dosing.pdf</>,
    <>Mushtaq T, Ali SR, Boulos N, et al. Emergency and perioperative management of adrenal insufficiency in children and young people: British Society for Paediatric Endocrinology and Diabetes consensus guidance. <em>Arch Dis Child</em>. 2023;108(11):871-878. doi:10.1136/archdischild-2022-325156</>,
    <>Tschaidse L, Wimmer S, Nowotny HF, et al. Frequency of stress dosing and adrenal crisis in paediatric and adult patients with congenital adrenal hyperplasia: a prospective study. <em>Eur J Endocrinol</em>. 2024;190(4):275-283. doi:10.1093/ejendo/lvae023</>,
  ], []);
  return (
      <div className={styles.container}>
        <div className={styles.anchor_btn_container}>
          <Button
            customClass={styles.anchor_btn}
            href="#gc-down-titration"
            onClick={(e) => scrollToAnchor(e, 'gc-down-titration')}
            data-gtm-event-category="Internal Link"
            data-gtm-event-action="Click"
            data-gtm-event-label="GC down-titration"
          >
            <span className={styles.anchor_btn_text}>
              GC down-titration
              <CustomImage
                src="/images/icons/arrow-right-circle.svg"
                width={23}
                height={23}
                alt=""
              />
            </span>
          </Button>
          <Button
            customClass={styles.anchor_btn}
            href="#gc-optimize"
            onClick={(e) => scrollToAnchor(e, 'gc-optimize')}
            data-gtm-event-category="Internal Link"
            data-gtm-event-action="Click"
            data-gtm-event-label="GC Dosing Calculators"
          >
            <span className={styles.anchor_btn_text}>
              GC dosing calculators
              <CustomImage
                src="/images/icons/arrow-right-circle.svg"
                width={23}
                height={23}
                alt=""
              />
            </span>
          </Button>
        </div>
        <HcpDosingVariesByAge />
        <HcpDosingPharmacokinetic />
        <HcpDosingAdministration />
        <HcpDosingDowntitration />
        <HcpDosingGcCalculator />
        <HcpBodySurfaceAreaCalculator />
        <HcpGcConversionCalculator />
        <HcpDosingAccessSimplified />
        {/* <HcpDosingReductions /> */}
        <section>
          <HcpLongButton
            iconType="Clinical-Information"
            label={<>Efficacy</>}
            description={<span className="inline-block max-w-[400px] leading-inherit">Review outcomes from CRENESSITY clinical trials.</span>}
            href="/clinical-information#trial-1-efficacy-link"
          />
          <HcpLongButton
            iconType="Contact-A-Rep"
            label={<>Get in Touch</>}
            description={<span className="inline-block max-w-[490px] leading-inherit">Sign up to schedule a call or visit with a Neurocrine representative.</span>}
            href="/support-resources#crm-sign-up"
          />
          <HcpLongButton
            iconType="Support-2"
            label={<>Support</>}
            description={<span className="inline-block max-w-[490px] leading-inherit">Explore information and resources for prescribing CRENESSITY.</span>}
            href="/support-resources#support"
          />
          <HcpLongButton
            iconType="PeerConnect"
            label={<>PeerConnect</>}
            description={<span className="inline-block max-w-[490px] leading-inherit">Connect with colleagues who treat CAH to consult, collaborate, or transition care.</span>}
            href="/peerconnect"
          />
        </section>
        <HcpPageFootnote>
          17-OHP=17-hydroxyprogesterone; ACTH=adrenocorticotropic hormone; CAH=congenital adrenal hyperplasia; CYP3A4=cytochrome P450 3A4; GC=glucocorticoid; ULN=upper limit of normal.
        </HcpPageFootnote>
        <HcpPageReferences references={references} />
      </div>
  );
}

HcpDosing.getLayoutProps = (_pageProps, _router) => ({
  customClass: styles.layout,
  meta: meta,
  hero: (
    <HcpHero
      theme="lilac"
      imageSrc="/images/hero/Dosing_Hero-Image.png"
      imageProps={{
        id: "hero_kidney_top",
        width: 476,
        height: 303,
      }}
    >
      TWICE-DAILY<br />
      ORAL DOSING
    </HcpHero>
  ),
});